import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    List,
    ListItem,
    ListItemText,
    Box,
    Chip,
    Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FunnelGeneration, Product } from '../../types';

interface FunnelHistoryDialogProps {
    open: boolean;
    onClose: () => void;
    historyGenerations: FunnelGeneration[];
    product?: Product;
    onLoadGeneration: (gen: FunnelGeneration) => void;
    isLoading: boolean;
}

const FunnelHistoryDialog: React.FC<FunnelHistoryDialogProps> = ({
                                                                     open,
                                                                     onClose,
                                                                     historyGenerations,
                                                                     product,
                                                                     onLoadGeneration,
                                                                     isLoading,
                                                                 }) => {
    const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
        switch (status) {
            case 'completed': return 'success';
            case 'failed': return 'error';
            case 'slides_generating':
            case 'prompt_generating': return 'primary';
            case 'prompt_ready': return 'info';
            default: return 'default';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('ru-RU', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getCompletedSlidesWithImages = (gen: FunnelGeneration) => {
        return gen.slide_requests?.filter(slide => slide.status === 'completed' && slide.image_url) || [];
    };


    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">
                        Funnel Generation History
                    </Typography>
                    {product && (
                        <Typography variant="body2" color="text.secondary">
                            for {product.name} {product.brand && `(${product.brand})`}
                        </Typography>
                    )}
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ color: (theme) => theme.palette.grey[500] }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {isLoading ? (
                    <Typography>Loading history...</Typography>
                ) : historyGenerations.length === 0 ? (
                    <Typography color="text.secondary">
                        No funnel generations found for this product.
                    </Typography>
                ) : (
                    <List>
                        {historyGenerations.map((gen, index) => {
                            const completedSlides = getCompletedSlidesWithImages(gen);

                            return (
                                <React.Fragment key={gen.id}>
                                    <ListItem
                                        onClick={() => onLoadGeneration(gen)}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            mb: 1,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                bgcolor: 'action.hover',
                                            },
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Typography variant="subtitle1">
                                                        Funnel #{gen.iteration_number || gen.id}
                                                    </Typography>
                                                    <Chip
                                                        label={gen.status}
                                                        size="small"
                                                        color={getStatusColor(gen.status)}
                                                    />
                                                    <Chip
                                                        label={gen.funnel_type.replace('_', ' ')}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        label={gen.target_marketplace}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            }
                                            secondary={
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Created: {formatDate(gen.created_at)}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Slides: {completedSlides.length} / {gen.slides_count} completed
                                                    </Typography>
                                                    {gen.total_cost && gen.total_cost > 0 && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Cost: ${gen.total_cost.toFixed(4)}
                                                        </Typography>
                                                    )}
                                                    {gen.error_message && (
                                                        <Typography variant="body2" color="error">
                                                            Error: {gen.error_message}
                                                        </Typography>
                                                    )}

                                                    {completedSlides.length > 0 && (
                                                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                                            {completedSlides.slice(0, 8).map((slide, i) => (
                                                                <img
                                                                    key={slide.image_url || i}
                                                                    // ✅ ИСПРАВЛЕНИЕ: Добавлен оператор '??' для обработки null/undefined
                                                                    src={slide.image_url ?? ''}
                                                                    alt={`Slide ${i + 1}`}
                                                                    style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                                                                />
                                                            ))}
                                                            {completedSlides.length > 8 && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    +{completedSlides.length - 8} more
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                    {index < historyGenerations.length - 1 && <Divider sx={{ my: 1 }} />}
                                </React.Fragment>
                            );
                        })}
                    </List>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default FunnelHistoryDialog;